import { useState } from "react";
import { PencilLine, Undo2 } from "lucide-react";
import {
  useCreate,
  useDelete,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
} from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";

import { crmErrorMessage } from "../misc/crmErrorMessage";
import type { Contact } from "../types";
import { TemperatureBadge } from "./TemperatureBadge";
import {
  CORRECTABLE_TEMPERATURES,
  isValidReason,
  type Temperature,
} from "./temperatureModel";

// [hiperautomacao] Replaces the free "status" of Atomic CRM: the seller
// may overrule the calculated temperature, but always with a reason and
// only for a limited time (the database enforces both).
export const CorrectTemperatureButton = () => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  const [isOpen, setIsOpen] = useState(false);
  if (!record) return null;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-7 cursor-pointer"
        onClick={() => setIsOpen(true)}
      >
        <PencilLine className="w-4 h-4" />
        {translate("hiper.temperature.correction.action")}
      </Button>
      {isOpen ? (
        <CorrectTemperatureDialog
          contactId={record.id}
          onClose={() => setIsOpen(false)}
        />
      ) : null}
    </>
  );
};

export const RemoveCorrectionButton = () => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [deleteOne, { isPending }] = useDelete();
  if (!record?.temperature_corrected) return null;

  const handleRemove = () => {
    deleteOne(
      "temperature_corrections",
      { id: record.id, previousData: { id: record.id } },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify("hiper.temperature.correction.removed", { type: "success" });
          refresh();
        },
        onError: (error) => {
          notify(crmErrorMessage(error), { type: "error" });
        },
      },
    );
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 cursor-pointer"
      onClick={handleRemove}
      disabled={isPending}
    >
      <Undo2 className="w-4 h-4" />
      {translate("hiper.temperature.correction.remove")}
    </Button>
  );
};

interface CorrectTemperatureDialogProps {
  contactId: Contact["id"];
  onClose: () => void;
}

const CorrectTemperatureDialog = ({
  contactId,
  onClose,
}: CorrectTemperatureDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [create, { isPending }] = useCreate();
  const [temperature, setTemperature] = useState<Temperature | "">("");
  const [reason, setReason] = useState("");
  const [hasTriedToSave, setHasTriedToSave] = useState(false);
  const isReasonValid = isValidReason(reason);

  const handleSave = () => {
    setHasTriedToSave(true);
    if (!temperature || !isReasonValid) return;
    create(
      "temperature_corrections",
      { data: { contact_id: contactId, temperature, reason: reason.trim() } },
      {
        onSuccess: () => {
          notify("hiper.temperature.correction.saved", { type: "success" });
          refresh();
          onClose();
        },
        onError: (error) => {
          notify(crmErrorMessage(error), { type: "error" });
        },
      },
    );
  };

  return (
    <Dialog open onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {translate("hiper.temperature.correction.title")}
          </DialogTitle>
          <DialogDescription>
            {translate("hiper.temperature.correction.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label>{translate("hiper.temperature.correction.value")}</Label>
            <RadioGroup
              value={temperature}
              onValueChange={(value) => setTemperature(value as Temperature)}
              aria-label={translate("hiper.temperature.correction.value")}
              className="flex flex-wrap gap-2"
            >
              {CORRECTABLE_TEMPERATURES.map((value) => (
                <Label
                  key={value}
                  className="flex items-center gap-2 rounded-md border px-3 py-2 font-normal cursor-pointer has-[[data-state=checked]]:border-primary"
                >
                  <RadioGroupItem
                    value={value}
                    aria-label={translate(`hiper.temperature.values.${value}`)}
                  />
                  <TemperatureBadge temperature={value} />
                </Label>
              ))}
            </RadioGroup>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="temperature-correction-reason">
              {translate("hiper.temperature.correction.reason")}
            </Label>
            <Textarea
              id="temperature-correction-reason"
              value={reason}
              placeholder={translate(
                "hiper.temperature.correction.reason_placeholder",
              )}
              aria-invalid={hasTriedToSave && !isReasonValid}
              onChange={(event) => setReason(event.target.value)}
            />
            {hasTriedToSave && !isReasonValid ? (
              <p className="text-sm text-destructive" role="alert">
                {translate("hiper.temperature.correction.reason_required")}
              </p>
            ) : null}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {translate("ra.action.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={!temperature || isPending}>
            {translate("hiper.temperature.correction.action")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
